// Locally.com dealer-locator embed for the V2 PDP.
//
// Picks up the per-page config from a `data-locally-config` JSON blob on the
// `#lcly-button-0` mount (server-rendered by `_DealerLocatorLocally.cshtml`),
// then 1) builds the locally config, 2) creates and appends `lcly-script-0`
// with `src` pointing at `map.js`, and 3) hooks a `MutationObserver` on the
// wrapping `.dealer-btn-container` so the `data-component-label` analytics
// attributes are added once locally injects its own DOM.
//
// Why we create the <script> dynamically rather than re-using a server-
// rendered placeholder: the HTML parser sets the "already started" flag on
// any empty `<script>` it inserts, after which assigning `src` from JS is a
// no-op and locally never fetches. Creating a fresh element with `src`
// already set sidesteps that lifecycle quirk entirely.
//
// Element IDs (`lcly-button-0` / `lcly-link-0` / `lcly-script-0`) match
// Locally's embed contract and must NOT be renamed.
//
// Locally sets its own `lg_session_v1` cookie (Cookiebot group: preferences),
// so `map.js` is only injected once that group is consented. Same wait-for-
// Cookiebot pattern as Bloomreach; V1 gates the equivalent inline script
// with `data-cookieconsent="preferences"`.
//
// Idempotent: bail when the mount element isn't on the page (e.g. the
// resolver returned a null `DealerLocatorLocally` payload), and skip
// recreating the script if a previous run already injected it.

const MOUNT_SELECTOR = '[data-locally-config]';
const SCRIPT_ID = 'lcly-script-0';
const CONTAINER_SELECTOR = '.dealer-btn-container';

type LocallyConfig = {
  buttonText: string;
  language: string;
  upca: string;
  locallyCategory: string;
};

const buildQueryString = (config: Record<string, string>): string =>
  Object.keys(config)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(config[key])}`)
    .join('&');

const initLocally = (mount: HTMLElement): void => {
  const raw = mount.dataset.locallyConfig;
  if (!raw) return;

  let cfg: LocallyConfig;
  try {
    cfg = JSON.parse(raw) as LocallyConfig;
  } catch {
    return;
  }

  // Locally expects keys in their flat `key=value&key2=value2` shape with
  // string values; the schema below mirrors V1's `lcly_config_0` blob.
  const lclyConfig: Record<string, string> = {
    company_name: 'Thule',
    button_text: cfg.buttonText ?? '',
    button_id: 'HTML',
    company_id: '49',
    css: '1',
    button_toggles_all: '1',
    upc: cfg.upca ?? '',
    show_related_styles: '1',
    n_related_styles: '3',
    show_location_switcher: '0',
    show_location_prompt: '0',
    lang: cfg.language ?? '',
    show_dealers: '1',
    n_dealers: '3',
    link_opens_modal: '1',
    category: cfg.locallyCategory ?? '',
  };

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://thule.locally.com/stores/map.js?${buildQueryString(lclyConfig)}`;
    document.body.appendChild(script);
  }

  // Analytics: locally injects its own anchors after the script loads;
  // mirror V1's MutationObserver so each gets a `data-component-label`.
  const container = document.querySelector(CONTAINER_SELECTOR);
  if (!container) return;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const target = mutation.target as HTMLElement | null;
      if (!target?.className) continue;

      if (target.className === 'lcly-anchor lcly-toggleable-0') {
        target.setAttribute('data-component-label', 'Show all dealers');
      } else if (typeof target.className === 'string' && target.className.includes('lcly-primary-trigger')) {
        target.setAttribute('data-component-label', 'Open dealer locator');
      }
    }
  });

  observer.observe(container, { childList: true, subtree: true });
};

const hasPreferencesConsent = (): boolean => window.Cookiebot?.consent?.preferences === true;

let initialized = false;
const initWhenConsented = (): void => {
  if (initialized || !hasPreferencesConsent()) return;
  initialized = true;
  document.querySelectorAll<HTMLElement>(MOUNT_SELECTOR).forEach(initLocally);
};

initWhenConsented();
['CookiebotOnConsentReady', 'CookiebotOnAccept'].forEach((e) => window.addEventListener(e, initWhenConsented));
